(() => {
  const wrap = (index, length) => (index + length) % length;
  const isModified = (event) => event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0;
  const canUseArrows = (target) => !target.closest('input, textarea, select, video, iframe, model-viewer');

  function initializeCard(gallery) {
    if (gallery.dataset.galleryReady) return;
    const slides = [...gallery.querySelectorAll('[data-card-slide]')];
    const controls = gallery.querySelector('[data-card-controls]');
    if (slides.length < 2 || !controls) return;
    gallery.dataset.galleryReady = 'true';
    controls.hidden = false;
    let index = 0;
    const update = (next) => {
      index = wrap(next, slides.length);
      slides.forEach((slide, slideIndex) => { slide.hidden = slideIndex !== index; });
      gallery.querySelector('[data-card-count]').textContent = String(index + 1);
    };
    gallery.querySelector('[data-card-prev]').addEventListener('click', () => update(index - 1));
    gallery.querySelector('[data-card-next]').addEventListener('click', () => update(index + 1));
    gallery.addEventListener('keydown', (event) => {
      if (!canUseArrows(event.target)) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        update(index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
    addSwipe(gallery.querySelector('.product-card__image-link'), () => update(index - 1), () => update(index + 1));
  }

  function addSwipe(element, previous, next) {
    if (!element) return;
    let start = null;
    let suppressClick = false;
    element.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      start = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }, { passive: true });
    element.addEventListener('touchend', (event) => {
      if (!start || !event.changedTouches.length) return;
      const deltaX = event.changedTouches[0].clientX - start.x;
      const deltaY = event.changedTouches[0].clientY - start.y;
      start = null;
      if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
        suppressClick = true;
        if (deltaX > 0) previous(); else next();
        window.setTimeout(() => { suppressClick = false; }, 450);
      }
    }, { passive: true });
    element.addEventListener('touchcancel', () => { start = null; }, { passive: true });
    element.addEventListener('click', (event) => {
      if (suppressClick) {
        event.preventDefault();
        event.stopImmediatePropagation();
        suppressClick = false;
      }
    }, true);
  }

  function initializeProduct(gallery) {
    if (gallery.dataset.galleryReady) return;
    gallery.dataset.galleryReady = 'true';
    const slides = [...gallery.querySelectorAll('[data-product-slide]')];
    if (!slides.length) return;
    const thumbs = [...gallery.querySelectorAll('[data-product-thumb]')];
    const navigation = gallery.querySelector('[data-product-nav]');
    let index = Math.max(0, slides.findIndex((slide) => slide.dataset.mediaId === gallery.dataset.initialMedia));
    const pause = (slide) => {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('iframe').forEach((frame) => {
        if (frame.hasAttribute('src')) frame.src = frame.src;
      });
      slide.querySelectorAll('model-viewer').forEach((model) => { if (typeof model.pause === 'function') model.pause(); });
    };
    const update = (next) => {
      const previous = index;
      index = wrap(next, slides.length);
      if (previous !== index) pause(slides[previous]);
      slides.forEach((slide, slideIndex) => { slide.hidden = slideIndex !== index; });
      thumbs.forEach((thumb) => {
        if (thumb.dataset.productThumb === slides[index].dataset.mediaId) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      const counter = gallery.querySelector('[data-product-count]');
      if (counter) counter.textContent = String(index + 1);
    };
    if (navigation) navigation.hidden = false;
    gallery.querySelector('[data-product-prev]')?.addEventListener('click', () => update(index - 1));
    gallery.querySelector('[data-product-next]')?.addEventListener('click', () => update(index + 1));
    thumbs.forEach((thumb) => thumb.addEventListener('click', (event) => {
      if (isModified(event)) return;
      event.preventDefault();
      update(slides.findIndex((slide) => slide.dataset.mediaId === thumb.dataset.productThumb));
    }));
    gallery.addEventListener('keydown', (event) => {
      if (!canUseArrows(event.target) || event.target.closest('dialog')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        update(index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
    slides.forEach((slide) => addSwipe(slide.querySelector('[data-image-zoom]'), () => update(index - 1), () => update(index + 1)));
    initializeZoom(gallery);
    update(index);
  }

  function initializeZoom(gallery) {
    const dialog = gallery.querySelector('[data-product-zoom]');
    const links = [...gallery.querySelectorAll('[data-image-zoom]')];
    if (!dialog || typeof dialog.showModal !== 'function' || !links.length) return;
    const image = dialog.querySelector('[data-zoom-image]');
    let index = 0;
    let opener = null;
    const update = (next) => {
      index = wrap(next, links.length);
      dialog.classList.remove('is-magnified');
      image.src = links[index].href;
      image.alt = links[index].dataset.zoomAlt || '';
      dialog.querySelector('[data-zoom-count]').textContent = `${index + 1} / ${links.length}`;
      dialog.scrollTop = 0;
      dialog.scrollLeft = 0;
    };
    links.forEach((link, linkIndex) => link.addEventListener('click', (event) => {
      if (isModified(event)) return;
      event.preventDefault();
      opener = link;
      update(linkIndex);
      dialog.showModal();
    }));
    dialog.querySelector('[data-zoom-prev]').addEventListener('click', () => update(index - 1));
    dialog.querySelector('[data-zoom-next]').addEventListener('click', () => update(index + 1));
    dialog.querySelector('.product-zoom__navigation').hidden = links.length < 2;
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        update(index + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => { opener?.focus({ preventScroll: true }); });
    image.addEventListener('click', () => dialog.classList.toggle('is-magnified'));
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-card-gallery]').forEach(initializeCard);
    scope.querySelectorAll('[data-product-gallery]').forEach(initializeProduct);
    scope.querySelectorAll('[data-variant-form]').forEach((form) => {
      if (form.dataset.variantReady) return;
      form.dataset.variantReady = 'true';
      form.querySelector('[data-variant-select]')?.addEventListener('change', () => {
        form.closest('[data-product-page]')?.querySelectorAll('.product-purchase-form button[type="submit"]').forEach((button) => {
          button.dataset.beforeVariantNavigationDisabled = String(button.disabled);
          button.disabled = true;
        });
        form.requestSubmit();
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-before-variant-navigation-disabled]').forEach((button) => {
      button.disabled = button.dataset.beforeVariantNavigationDisabled === 'true';
      delete button.dataset.beforeVariantNavigationDisabled;
    });
  });
})();
